import { useEffect, useState } from 'react'
import type { BackofficeUser, UserAdminDetailsResponse } from 'api/generated'
import { Button } from 'components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Skeleton } from 'components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import {
  clientSummary,
  formatDay,
  KEY_METRICS,
  LOGIN_METHOD_NAMES,
  timeAgo,
} from 'helpers/user-details'
import { SOURCE_LABELS } from 'helpers/user-filters'
import { ExternalLink } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store/store'
import { fetchUserDetails } from 'store/users/usersSlice'
import type { UserSearchInfo } from 'types/users'
import { SITE_URL, UserAvatar, UserBadges } from './UserCells'
import { NotificationsTab, TimelineTab } from './UserDetailsFeeds'
import { InfoTile, Section } from './UserDetailsParts'
import {
  AccessTab,
  AccountTab,
  ActivityTab,
  FitTab,
  MembershipTab,
} from './UserDetailsTabs'

/** Tabs that don't need the admin card. */
const STANDALONE_TABS = ['account', 'timeline', 'notifications']

interface Props {
  /** A row of the users page (with `search`) or a plain backoffice user. */
  user: BackofficeUser & { search?: UserSearchInfo }
  open: boolean
  onOpenChange: (open: boolean) => void
}

const Overview = ({
  user,
  details,
}: {
  user: BackofficeUser
  details: UserAdminDetailsResponse
}) => {
  const admin = details.user.admin
  const client = clientSummary(admin)
  const player = details.user.fit_player_profile
  const subscription = details.membership.subscriptions.find(
    ({ status }) => status === 'active'
  )

  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
        <InfoTile
          label='Usa'
          value={client.label}
          hint={client.source}
          tone={client.kind ? undefined : 'warn'}
        />
        <InfoTile
          label='Ultima attività'
          value={admin?.last_active_at ? timeAgo(admin.last_active_at) : '—'}
          hint={
            admin?.last_sign_in_at &&
            `login ${formatDay(admin.last_sign_in_at)}${
              admin.last_sign_in_method
                ? ` con ${LOGIN_METHOD_NAMES[admin.last_sign_in_method]}`
                : ''
            }`
          }
        />
        <InfoTile
          label='Iscritto'
          value={formatDay(user.created_at)}
          hint={[
            timeAgo(user.created_at),
            admin?.signup_source && `da ${SOURCE_LABELS[admin.signup_source]}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <InfoTile
          label='Premium'
          value={
            admin?.premium
              ? `fino al ${formatDay(user.premium_expiration)}`
              : 'No'
          }
          tone={admin?.premium ? 'good' : undefined}
          hint={
            subscription
              ? `Recharge, prossimo addebito ${formatDay(subscription.next_charge_at)}`
              : `${admin?.premium_purchases_count ?? 0} codici attivati`
          }
        />
        <InfoTile
          label='Giocatore FIT'
          value={player ? `${player.rank ?? '—'} · ${player.full_name}` : 'No'}
          hint={player?.club?.name}
        />
        <InfoTile
          label='Sessioni attive'
          value={details.sessions.count}
          hint={
            details.sessions.first_at &&
            `la più vecchia del ${formatDay(details.sessions.first_at)}`
          }
        />
      </div>

      <Section title='Attività (totale · ultimi 30 giorni)'>
        <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
          {KEY_METRICS.map(({ key, label }) => {
            const counter = details.activity[key]
            return (
              <InfoTile
                key={key}
                label={label}
                value={counter.total}
                hint={
                  counter.total
                    ? `+${counter.last_30_days} · ${timeAgo(counter.last_at)}`
                    : 'mai'
                }
              />
            )
          })}
        </div>
      </Section>

      {details.membership.subscriptions.some(
        ({ in_recovery }) => in_recovery
      ) && (
        <InfoTile
          label='Attenzione'
          value='Addebito Recharge fallito'
          hint='Recharge sta riprovando: vedi Membership.'
          tone='bad'
        />
      )}
    </div>
  )
}

const Loading = () => (
  <div className='space-y-3'>
    <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className='h-20' />
      ))}
    </div>
    <Skeleton className='h-40' />
  </div>
)

/**
 * The user card: row data at once, the rest (activity, membership, FIT,
 * sessions) loaded from GET /users/:id/admin-details when it opens.
 */
export const UserDetailsDialog = ({ user, open, onOpenChange }: Props) => {
  const dispatch = useAppDispatch()
  const details = useAppSelector((state) => state.users.details)
  const current = details.userId === user.id ? details : null
  const [tab, setTab] = useState('overview')
  // Without the search data of a row, the header takes it from the card
  const search = user.search ?? current?.data?.user
  const header = { ...user, search }

  useEffect(() => {
    if (open) dispatch(fetchUserDetails(user.id))
  }, [open, user.id, dispatch])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[94dvh] w-[calc(100%-1rem)] max-w-4xl flex-col gap-0 overflow-hidden p-0 sm:max-w-4xl'>
        <DialogHeader className='border-b p-4 pe-12 text-left'>
          <div className='flex items-start gap-3'>
            <UserAvatar user={header} className='size-14' />
            <div className='min-w-0 flex-1 space-y-1'>
              <DialogTitle className='truncate'>
                {user.full_name || '(senza nome)'}
              </DialogTitle>
              <DialogDescription className='truncate'>
                #{user.id} · {user.email}
              </DialogDescription>
              <UserBadges user={header} />
            </div>
            <Button
              variant='outline'
              size='sm'
              className='hidden sm:inline-flex'
              asChild
            >
              <a
                href={`${SITE_URL}/utente/${user.id}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <ExternalLink /> Profilo
              </a>
            </Button>
          </div>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={setTab}
          className='min-h-0 flex-1 gap-0'
        >
          {/* Six tabs don't fit a phone: the list scrolls sideways */}
          <div className='shrink-0 overflow-x-auto border-b px-4 py-2'>
            <TabsList>
              <TabsTrigger value='overview'>Panoramica</TabsTrigger>
              <TabsTrigger value='activity'>Attività</TabsTrigger>
              <TabsTrigger value='membership'>Membership</TabsTrigger>
              <TabsTrigger value='fit'>FIT</TabsTrigger>
              <TabsTrigger value='timeline'>Timeline</TabsTrigger>
              <TabsTrigger value='notifications'>Notifiche</TabsTrigger>
              <TabsTrigger value='access'>Accessi</TabsTrigger>
              <TabsTrigger value='account'>Account</TabsTrigger>
            </TabsList>
          </div>
          <div className='min-h-0 flex-1 overflow-y-auto p-4'>
            {/* Loaded when their tab opens, not with the card */}
            <TabsContent value='timeline'>
              <TimelineTab userId={user.id} />
            </TabsContent>
            <TabsContent value='notifications'>
              <NotificationsTab userId={user.id} />
            </TabsContent>
            <TabsContent value='account'>
              <AccountTab user={user} />
            </TabsContent>
            {/* Account, timeline and notifications don't wait for the card */}
            {STANDALONE_TABS.includes(tab) ? null : current?.error ? (
              <div className='space-y-2'>
                <p className='text-red-500'>{current.error}</p>
                <Button
                  variant='outline'
                  onClick={() => dispatch(fetchUserDetails(user.id))}
                >
                  Riprova
                </Button>
              </div>
            ) : !current?.data ? (
              <Loading />
            ) : (
              <>
                <TabsContent value='overview'>
                  <Overview user={user} details={current.data} />
                </TabsContent>
                <TabsContent value='activity'>
                  <ActivityTab details={current.data} />
                </TabsContent>
                <TabsContent value='membership'>
                  <MembershipTab user={user} details={current.data} />
                </TabsContent>
                <TabsContent value='fit'>
                  <FitTab details={current.data} />
                </TabsContent>
                <TabsContent value='access'>
                  <AccessTab details={current.data} />
                </TabsContent>
              </>
            )}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
